import React from 'react';
import { formatDisplayDate } from '../../services/dateUtils';
import HelpAsk from '../../help/HelpAsk';
import { HelpOffer } from '../../help/HelpOffer';

export type EditScope = 'one-off' | 'persist' | 'cancel';
export type EditKind = 'update' | 'resize' | 'delete' | 'move-day';

interface Props {
  open: boolean;
  kind: EditKind;
  /** Calendar date the edit was triggered on (e.g. 2026-04-22). */
  editDate: string | null;
  /** Inline error message rendered above the choice buttons. Set when the
   *  attendance-block guard rejects the chosen scope so the user sees why
   *  their action stopped without losing the modal context. */
  errorMessage?: string | null;
  /** A How To to offer beside that error — the way out of the block. */
  errorHelp?: HelpOffer | null;
  /** The edit was already saved as "this date and future" without asking,
   *  and was refused. Only the reason and Cancel are shown — "Just this date"
   *  can't hold the change, and retrying would be refused the same way. */
  persistOnly?: boolean;
  onChoice: (scope: EditScope) => void;
}

const KIND_TITLES: Record<EditKind, string> = {
  update:     'Apply changes to:',
  resize:     'Apply new time to:',
  delete:     'Cancel session for:',
  'move-day': 'Move session for:',
};

const ONE_OFF_LABELS: Record<EditKind, string> = {
  update:     'Just this date',
  resize:     'Just this date',
  delete:     'Just this date',
  'move-day': 'Just this date',
};

const PERSIST_LABELS: Record<EditKind, string> = {
  update:     'This date and future',
  resize:     'This date and future',
  delete:     'End the rule from this date',
  'move-day': 'This date and future',
};

const ScopeChoiceModal: React.FC<Props> = ({ open, kind, editDate, errorMessage, errorHelp, persistOnly = false, onChoice }) => {
  if (!open) return null;

  const dateLabel = editDate ? formatDisplayDate(editDate) : 'this occurrence';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="scope-choice-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onChoice('cancel');
      }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 8, padding: '1.25rem 1.5rem',
          width: 'min(420px, 92vw)', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h6 id="scope-choice-title" className="mb-2" style={{ fontWeight: 700 }}>
          {persistOnly ? "Changes couldn't be saved" : KIND_TITLES[kind]}
        </h6>
        {!persistOnly && (
          <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
            {kind === 'delete'
              ? `Past dates of this rule will not be affected.`
              : `Past dates will keep the existing rule.`}{' '}
            Date: <strong>{dateLabel}</strong>.
          </p>
        )}

        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 6,
              background: 'rgba(184, 48, 47, 0.08)',
              border: '1px solid rgba(184, 48, 47, 0.32)',
              color: '#7a1f1d',
              fontSize: '0.82rem',
              lineHeight: 1.4,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
            }}
          >
            <i className="fas fa-circle-exclamation" style={{ color: '#b8302f', marginTop: 2 }} aria-hidden />
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
              <span>{errorMessage}</span>
              {errorHelp && <HelpAsk guideKey={errorHelp.guideKey} label={errorHelp.label} variant="inline" />}
            </span>
          </div>
        )}

        {!persistOnly && (
          <div className="d-flex flex-column gap-2">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => onChoice('one-off')}
            >
              {ONE_OFF_LABELS[kind]}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onChoice('persist')}
            >
              {PERSIST_LABELS[kind]}
            </button>
          </div>
        )}

        <div className="d-flex justify-content-end mt-3">
          <button
            type="button"
            className="btn btn-link btn-sm text-muted"
            onClick={() => onChoice('cancel')}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScopeChoiceModal;
